import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import { invalidateDraftSurfaces } from '@/api/accounts-receivable/sales-order-drafts/invalidateDraftSurfaces';
import type { CreateDiscountInput, CreateDiscountResponse } from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

/**
 * Hook for creating a new discount.
 * Handles the API call, success/error states, and cache invalidation.
 *
 * Note: Price status badge updates are handled by the caller (e.g.,
 * CreateDiscountModal) via the single-item validation endpoint, NOT by
 * broad query invalidation here.
 */
export function useCreateDiscount() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<CreateDiscountResponse, Error, CreateDiscountInput>({
    mutationFn: async (input) => {
      return await rpcClient.accountsReceivable.discounts.create(input);
    },
    onSuccess: (_data, variables) => {
      showToast({
        title: 'Success',
        message: 'Discount created successfully',
        color: 'success',
        icon: 'check',
      });

      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.discounts.list.queryKey({
          input: {},
        }),
      });

      if (variables.stagedPurchaseOrderId) {
        invalidateDraftSurfaces(queryClient, variables.stagedPurchaseOrderId);
      }
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to create discount',
        color: 'error',
        icon: 'x',
      });
    },
  });
}
